import { env } from "@pos-vidyon/env/web";
import { createAuthClient } from "better-auth/react";
import { organizationClient, adminClient, usernameClient } from "better-auth/client/plugins";
import { queryOptions } from "@tanstack/react-query";

function getServerUrl(url: string) {
  const normalized = url.endsWith("/") ? url.slice(0, -1) : url;

  if (!normalized.startsWith("/")) {
    return normalized;
  }

  if (typeof window !== "undefined") {
    return `${window.location.origin}${normalized}`;
  }

  const processEnv = (
    globalThis as {
      process?: { env?: Record<string, string | undefined> };
    }
  ).process?.env;
  const vercelUrl =
    processEnv?.VERCEL_ENV === "production"
      ? (processEnv?.VERCEL_PROJECT_PRODUCTION_URL ?? processEnv?.VERCEL_URL)
      : (processEnv?.VERCEL_URL ?? processEnv?.VERCEL_PROJECT_PRODUCTION_URL);
  if (vercelUrl) {
    const origin = vercelUrl.startsWith("http") ? vercelUrl : `https://${vercelUrl}`;
    return `${origin}${normalized}`;
  }

  return `http://localhost:3000${normalized}`;
}
export const sessionQueryOptions = queryOptions({
  queryKey: ["auth-session"],
  queryFn: async () => {
    const result = await authClient.getSession();
    // If the fetch succeeded but returned an error (e.g. network layer resolved
    // but server indicated failure), attach metadata so the auth guard can
    // distinguish "no session" from "network failure".
    if (result.error) {
      const err = result.error as Record<string, unknown>;
      // If it's a network/fetch error (not a 401), mark it so the guard doesn't
      // mistakenly redirect to login.
      if (err.status !== 401 && err.status !== 403) {
        (result as any).__networkError = true;
      }
    }
    return result;
  },
  staleTime: 60_000, // 60s - reduce unnecessary refetches on navigation
  gcTime: 5 * 60 * 1000,
  retry: (failureCount, error) => {
    // Don't retry on auth errors (401/403) - those are genuine "not logged in"
    const status = (error as any)?.status;
    if (status === 401 || status === 403) return false;
    // Retry up to 3 times for network errors
    return failureCount < 3;
  },
  retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
});

export const authClient = createAuthClient({
  // better-auth derives its route-matching base from this URL's path, so the
  // public auth path must equal the server-side mount (/api/auth everywhere)
  baseURL: new URL("/api/auth", getServerUrl(env.VITE_SERVER_URL)).toString(),
  plugins: [organizationClient(), adminClient(), usernameClient()],
  fetchOptions: {
    onRequest: (context) => {
      const token = typeof window !== "undefined" ? localStorage.getItem("bearer_token") : null;
      if (token) {
        if (context.headers instanceof Headers) {
          context.headers.set("Authorization", `Bearer ${token}`);
        } else if (context.headers) {
          (context.headers as Record<string, string>)["Authorization"] = `Bearer ${token}`;
        } else {
          context.headers = new Headers({ Authorization: `Bearer ${token}` });
        }
      }
      const adminSession =
        typeof window !== "undefined" ? localStorage.getItem("admin_session") : null;
      if (adminSession) {
        if (context.headers instanceof Headers) {
          context.headers.set("x-admin-session", adminSession);
        } else if (context.headers) {
          (context.headers as Record<string, string>)["x-admin-session"] = adminSession;
        } else {
          context.headers = new Headers({ "x-admin-session": adminSession });
        }
      }

      const getIsDesktop = () => {
        if (typeof window === "undefined") return false;
        if (window.location.protocol === "views:") return true;
        if (
          typeof navigator !== "undefined" &&
          navigator.userAgent.toLowerCase().includes("electrobun")
        )
          return true;
        if (window.location.search.includes("platform=desktop")) return true;
        try {
          const storage = localStorage.getItem("terminal-storage");
          if (storage) {
            const parsed = JSON.parse(storage);
            return parsed?.state?.platform === "desktop";
          }
        } catch {}
        return false;
      };
      const isDesktop = getIsDesktop();
      if (isDesktop) {
        if (context.headers instanceof Headers) {
          context.headers.set("x-client-type", "desktop");
        } else if (context.headers) {
          (context.headers as Record<string, string>)["x-client-type"] = "desktop";
        } else {
          context.headers = new Headers({ "x-client-type": "desktop" });
        }
      }
      return context;
    },
    onResponse: (context) => {
      const token = context.response.headers.get("set-auth-token");
      console.log("[auth-client] onResponse set-auth-token:", token);
      if (token) {
        if (typeof window !== "undefined") {
          localStorage.setItem("bearer_token", token);
        }
      }
      const adminSession = context.response.headers.get("x-admin-session");
      if (adminSession) {
        if (typeof window !== "undefined") {
          localStorage.setItem("admin_session", adminSession);
        }
      }
      const requestUrl = context.request.url.toString();
      if (typeof window !== "undefined") {
        if (requestUrl.endsWith("/sign-out")) {
          localStorage.removeItem("bearer_token");
          localStorage.removeItem("admin_session");
        } else if (requestUrl.includes("/stop-impersonating")) {
          // After stopImpersonating the server returns a new session token for the
          // admin via set-auth-token, so keep bearer_token and only drop the bridge.
          localStorage.removeItem("admin_session");
        }
      }
      return context;
    },
  },
});
